import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { updateProfile } from 'firebase/auth';
import { useAuth } from '../../auth/AuthContext';
import { auth } from '../../auth/firebase';
import { useUserRole } from '../../hooks/useUserRole';
import { useDashboard } from '../../context/DashboardContext';
import { useGoalsConfig } from '../../utils/goalsLocalStore';
import { fetchStylists } from '../../api/dashboardApi';
import { bindMyIdentity, declareNotStylist } from '../../api/authApi';
import { trackTutorialComplete } from '../../analytics/appAnalytics';
import { useNavigateWithFilters } from '../../utils/dashboardNav';
import { filterPosNames, hasSavedKgi } from '../../utils/dashboardFilters';

const PLACEHOLDERS = new Set(['フリー', 'ﾌﾘｰ', 'free', 'FREE']);

/**
 * First-visit gate for the signed-in person's OWN record.
 * - profile === undefined  → not fetched yet          → render nothing
 * - profile === null       → /auth/me failed (404/5xx), dev or no role → render nothing
 * - is_stylist === false   → company_admin declared「スタイリストではない」→ never shows
 * - unbound                → identity card (store → name → 完了)
 * - bound, no saved KGI    → goal card (目標設定する → /settings/goaledit?edit=goal)
 * Goals are always read for profile.home_store / profile.stylist_name_pos, never
 * from the URL, so viewing a colleague cannot open this overlay.
 */
export default function FirstVisitOverlay() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigateWithFilters();
  const { user, refreshUser } = useAuth();
  const { role, loading: roleLoading } = useUserRole();
  // /auth/me is fetched once in DashboardContext (it also picks the main store on open)
  const { stores, myProfile: profile, setMyProfile: setProfile } = useDashboard();

  const [posNames, setPosNames] = useState([]);
  const [namesLoading, setNamesLoading] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [posName, setPosName] = useState('');
  const [homeStore, setHomeStore] = useState('');
  const [saving, setSaving] = useState(false);
  const [awaitGoals, setAwaitGoals] = useState(false);
  const [thinkText, setThinkText] = useState('');
  const [error, setError] = useState('');

  const bound = Boolean(profile?.stylist_name_pos && profile?.home_store);
  const notStylist = profile?.is_stylist === false;
  const ownStore = bound ? profile.home_store : null;
  const ownStylist = bound ? profile.stylist_name_pos : null;
  const [goals, , goalsMeta] = useGoalsConfig(user?.uid, ownStore, ownStylist);

  const onGoaledit = location.pathname.startsWith('/settings/goaledit');
  const skipRole = roleLoading || !role || role === 'dev';

  // ---- goals settled flag (avoid flash from uid-cached local goals) --------
  const sawGoalsLoadingRef = useRef(false);
  useEffect(() => {
    if (goalsMeta.loading) sawGoalsLoadingRef.current = true;
  }, [goalsMeta.loading]);
  useEffect(() => {
    sawGoalsLoadingRef.current = false;
  }, [ownStore, ownStylist]);
  const goalsSettled = bound && sawGoalsLoadingRef.current && !goalsMeta.loading;

  // ---- suggestions: names of the chosen store only -------------------------
  useEffect(() => {
    if (skipRole || bound || notStylist || !homeStore) {
      setPosNames([]);
      return undefined;
    }
    let cancelled = false;
    setNamesLoading(true);
    fetchStylists(homeStore)
      .then((res) => {
        if (cancelled) return;
        setPosNames((res.stylists || []).filter((n) => n && !PLACEHOLDERS.has(n)));
      })
      .catch(() => {
        if (!cancelled) setPosNames([]);
      })
      .finally(() => {
        if (!cancelled) setNamesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [skipRole, bound, notStylist, homeStore]);

  // ---- after 完了: wait for the profile-scoped goals fetch ------------------
  useEffect(() => {
    if (!awaitGoals || !goalsSettled) return;
    setAwaitGoals(false);
    setSaving(false);
    setThinkText('保存しました。目標設定に進めます。');
  }, [awaitGoals, goalsSettled]);

  const suggestions = useMemo(
    () => (posName ? [] : filterPosNames(nameInput, posNames).slice(0, 8)),
    [nameInput, posNames, posName]
  );
  // 「POSにまだ名前がありません」: the typed name is registered as-is (new staff, no Bionly rows yet).
  // Numbers stay empty until POS exports that exact string; goals / 注力 work at once (owner 2026-10-23).
  const [manualName, setManualName] = useState(false);
  const typedName = nameInput.trim();
  const bindName = posName || (manualName ? typedName : '');
  const canDone = Boolean(bindName && homeStore) && !saving;
  const profileKnown = profile !== undefined && profile !== null;
  const gateOff = skipRole || onGoaledit || !profileKnown || notStylist;
  const identityOpen = !gateOff && !bound;
  // setupStep (初期設定 in progress) keeps the card up until every step is done
  const setupUnfinished = Boolean(goals?.setupStep && goals.setupStep !== 'done');
  const goalOpen = !gateOff && bound && goalsSettled && !goalsMeta.error && (!hasSavedKgi(goals) || setupUnfinished);
  const show = identityOpen || goalOpen || saving;

  const onStoreChange = (v) => {
    setHomeStore(v);
    setNameInput('');
    setPosName('');
    setManualName(false);
  };
  const onPickName = (n) => {
    setPosName(n);
    setNameInput(n);
    setManualName(false);
  };
  const onNameChange = (v) => {
    setNameInput(v);
    setPosName(posNames.includes(v.trim()) ? v.trim() : '');
    setManualName(false);
  };

  const onDone = async () => {
    if (!canDone) return;
    setError('');
    setSaving(true);
    setThinkText('氏名と所属店舗を保存中…');
    try {
      const next = await bindMyIdentity({ stylistNamePos: bindName, homeStore, noPosData: !posName });
      if (auth.currentUser) {
        try {
          await updateProfile(auth.currentUser, { displayName: bindName });
        } catch (e) {
          console.warn('[fvsetup] client displayName update failed', e);
        }
        if (refreshUser) await refreshUser();
      }
      setThinkText('目標データを読み込み中…');
      if (!onGoaledit) {
        setSearchParams(
          (prev) => {
            const sp = new URLSearchParams(prev);
            sp.set('store', homeStore);
            sp.set('stylist', bindName);
            return sp;
          },
          { replace: true }
        );
      }
      setAwaitGoals(true);
      setProfile(next); // switches useGoalsConfig to own store/stylist → loading → settled
      trackTutorialComplete();
    } catch (err) {
      setError(err?.message || '保存に失敗しました');
      setThinkText('');
      setSaving(false);
      setAwaitGoals(false);
    }
  };

  const onNotStylist = async () => {
    if (saving) return;
    setError('');
    setSaving(true);
    setThinkText('保存中…');
    try {
      const next = await declareNotStylist();
      setProfile(next);
    } catch (err) {
      setError(err?.message || '保存に失敗しました');
    } finally {
      setThinkText('');
      setSaving(false);
    }
  };

  const setupReady = !saving && !awaitGoals && bound && goalsSettled;

  const onSetup = () => {
    if (!setupReady) return;
    navigate(
      `/settings/goaledit?edit=goal&store=${encodeURIComponent(profile.home_store)}&stylist=${encodeURIComponent(
        profile.stylist_name_pos
      )}`
    );
  };

  if (!show) return null;

  return (
    <div className="fvsetup-overlay" role="dialog" aria-modal="true" aria-labelledby="fvsetup-title">
      <div className="fvsetup-card">
        <div className="fvsetup-ic">🎯</div>
        <div className="fvsetup-eyebrow">初回セットアップ</div>
        <div className="fvsetup-title" id="fvsetup-title">
          AIで目標を設定しましょう
        </div>
        <div className="fvsetup-body">
          {bound
            ? 'まだ最終ゴールが未設定です。AIマネージャーと一緒に、単月売上のゴールから始めます。'
            : '先にあなたの所属店舗とPOS名を保存します。完了するまで目標設定には進めません。'}
        </div>

        {!bound && (
          <>
            <label className="fvsetup-label">
              所属店舗（主表示）
              <select
                className="fvsetup-input"
                value={homeStore}
                disabled={saving}
                onChange={(e) => onStoreChange(e.target.value)}
              >
                <option value="">選択してください</option>
                {(stores || []).map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>

            <label className="fvsetup-label">
              氏名（POSの名前）
              <input
                className="fvsetup-input"
                value={nameInput}
                disabled={saving || !homeStore}
                placeholder={homeStore ? 'フルネームを入力' : '先に所属店舗を選択'}
                autoComplete="off"
                onChange={(e) => onNameChange(e.target.value)}
              />
            </label>
            {namesLoading ? <div className="fvsetup-hint">店舗のスタイリスト一覧を取得中…</div> : null}
            {suggestions.length > 0 && (
              <div className="fvsetup-suggest">
                {suggestions.map((n) => (
                  <button type="button" key={n} className="fvsetup-sug" onClick={() => onPickName(n)}>
                    {n}
                  </button>
                ))}
              </div>
            )}
            {posName ? <div className="fvsetup-picked">POS名: {posName}</div> : null}
            {!posName && !namesLoading && homeStore && typedName && !manualName && (
              <>
                <div className="fvsetup-hint">この店舗のPOSに「{typedName}」は見つかりません。</div>
                <button type="button" className="fvsetup-link" disabled={saving} onClick={() => setManualName(true)}>
                  POSにまだ名前がありません — 「{typedName}」で登録する
                </button>
              </>
            )}
            {manualName && !posName ? (
              <div className="fvsetup-picked">
                「{typedName}」で登録します。POSに同じ名前で売上が入ると自動でつながります（それまで実績は空のまま。目標は設定できます）。
              </div>
            ) : null}

            <button type="button" className="fvsetup-cta" disabled={!canDone} onClick={onDone}>
              完了
            </button>
            {role === 'company_admin' && (
              <button type="button" className="fvsetup-link" disabled={saving} onClick={onNotStylist}>
                スタイリストではない（経営者のみ）
              </button>
            )}
          </>
        )}

        {(saving || thinkText) && (
          <div className="fvsetup-think">
            <span className="fvsetup-dots" aria-hidden="true">
              <span />
              <span />
              <span />
            </span>
            {thinkText || '保存して読み込み中…'}
          </div>
        )}

        {error ? <div className="fvsetup-err">{error}</div> : null}

        <button type="button" className="fvsetup-cta fvsetup-cta--next" disabled={!setupReady} onClick={onSetup}>
          目標設定する
        </button>
        <div className="fvsetup-hint">
          {setupReady
            ? '目標設定すると最終ゴールのAI相談が開きます'
            : bound
              ? 'この画面は設定するまで閉じられません'
              : 'この画面は設定するまで閉じられません。氏名を間違えた場合は管理者へ連絡してください。'}
        </div>
      </div>
    </div>
  );
}
